import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Search, ShieldCheck, AlertCircle, Layers, BadgeCheck, ScanLine, Zap, BarChart3, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import api from "../lib/api";
import SlabPhoto from "../components/SlabPhoto";
import SlabComingSoon from "../components/SlabComingSoon";
import Seo from "../components/Seo";

const EXAMPLES = ["8000152605", "8000152601", "8000152604"];

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-4 py-3 border-b border-neutral-100 last:border-0">
      <span className="text-xs font-bold uppercase tracking-[0.12em] text-neutral-400">{label}</span>
      <span className="text-sm font-bold text-[#0A0A0A] text-right" data-testid={`field-${label.toLowerCase().replace(/\s/g, "-")}`}>{value}</span>
    </div>
  );
}

export default function Lookup() {
  const { certnum } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [q, setQ] = useState(certnum || sp.get("cert") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const doSearch = async (value) => {
    const cn = (value || "").trim();
    if (!cn) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.get(`/cert/${encodeURIComponent(cn)}`);
      setResult(data);
    } catch (e) {
      setError(e.response?.status === 404 ? "No certification found for that number. Double-check the digits and try again." : "Lookup failed. Please try again.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const c = certnum || sp.get("cert");
    if (c) { setQ(c); doSearch(c); }
    /* eslint-disable-next-line */
  }, [certnum, sp]);

  const submit = (e) => { e.preventDefault(); if (q.trim()) { nav(`/lookup/${encodeURIComponent(q.trim())}`); doSearch(q); } };

  return (
    <div className="bg-white">
      <Seo title="Verify a Card Grade — Cert Lookup & Population Report" description="Look up and verify any MAS card grade by certification number. Instantly confirm authenticity, the grade, card details, and live population data." path="/lookup" />
      {/* BLACK HERO */}
      <section className="relative bg-[#0A0A0A] text-white overflow-hidden">
        <span className="pointer-events-none select-none absolute -right-8 top-2 font-display font-900 text-[13rem] md:text-[17rem] leading-none text-white/[0.04]" style={{ fontWeight: 900 }}>CERT</span>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-32">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/45">Real-Time Verification</span>
          <h1 className="font-display font-900 text-5xl md:text-7xl tracking-tight mt-4 max-w-3xl leading-[0.92]" style={{ fontWeight: 900 }}>MAS Grading Lookup</h1>
          <p className="text-white/60 mt-5 text-lg max-w-xl">Confirm a graded card's authenticity and grade by searching its MAS certification number.</p>
        </div>
      </section>

      {/* FLOATING SEARCH CARD */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 -mt-20 relative z-20">
        <div className="bg-white rounded-3xl border border-neutral-200 shadow-[0_30px_70px_-25px_rgba(0,0,0,0.35)] p-6 md:p-8">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-500 mb-3"><ScanLine size={16} /> Enter the certification number</div>
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" size={18} />
              <input data-testid="cert-lookup-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. 8000152601"
                className="w-full bg-neutral-50 border border-neutral-200 rounded-full pl-12 pr-4 py-3.5 text-[#0A0A0A] placeholder:text-neutral-400 focus:border-[#0A0A0A] focus:bg-white outline-none transition-all duration-200" />
            </div>
            <button data-testid="cert-lookup-submit" type="submit" disabled={loading}
              className="px-9 py-3.5 bg-[#0A0A0A] text-white font-bold rounded-full hover:bg-neutral-800 transition-colors duration-300 disabled:opacity-60 whitespace-nowrap inline-flex items-center justify-center gap-2">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} {loading ? "Searching…" : "Search"}
            </button>
          </form>
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <span className="text-xs text-neutral-400 font-semibold">Try:</span>
            {EXAMPLES.map((ex) => (
              <button key={ex} data-testid={`try-${ex}`} onClick={() => { setQ(ex); nav(`/lookup/${ex}`); doSearch(ex); }}
                className="text-xs font-mono font-semibold bg-neutral-100 hover:bg-neutral-900 hover:text-white text-neutral-600 px-3 py-1.5 rounded-lg transition-colors duration-200">{ex}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16 md:py-20">
        {/* ERROR */}
        {error && (
          <div data-testid="lookup-error" className="flex items-center gap-3 text-neutral-700 bg-neutral-50 border border-neutral-200 rounded-2xl p-5 max-w-2xl mx-auto">
            <AlertCircle className="text-[#D4AF37]" size={20} /> {error}
          </div>
        )}

        {/* RESULT */}
        {result && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="space-y-8">
            {/* Header */}
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 px-4 py-1.5 text-xs font-bold uppercase tracking-widest"><BadgeCheck size={15} /> Verified & Certified by MAS</div>
              <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-4 text-[#0A0A0A]">{[result.cert.year, result.cert.brand, result.cert.subject].filter(Boolean).join(" ")}</h2>
            </div>

            {/* POPULATION — full-width skinny band */}
            <div className="bg-[#0A0A0A] text-white rounded-3xl p-6 md:p-7" data-testid="population-report">
              <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                <div className="flex items-center gap-2 lg:w-52 shrink-0"><Layers className="text-white" size={18} /><span className="font-display font-bold text-lg">Population Report</span></div>
                <div className="grid grid-cols-3 gap-3 lg:w-[360px] shrink-0">
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-3 text-center">
                    <div className="font-display font-900 text-2xl" style={{ fontWeight: 900 }} data-testid="pop-total">{result.population.total_population}</div>
                    <div className="text-[9px] uppercase tracking-widest text-white/45 mt-1">Total Pop</div>
                  </div>
                  <div className="gem-foil rounded-2xl p-3 text-center text-[#3a2e05]">
                    <div className="font-display font-900 text-2xl" style={{ fontWeight: 900 }} data-testid="pop-this-grade">{result.population.grade_population}</div>
                    <div className="text-[9px] uppercase tracking-widest font-bold mt-1">This Grade</div>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-3 text-center">
                    <div className="font-display font-900 text-2xl" style={{ fontWeight: 900 }} data-testid="pop-higher">{result.population.higher_count}</div>
                    <div className="text-[9px] uppercase tracking-widest text-white/45 mt-1">Higher</div>
                  </div>
                </div>
                <div className="flex-1 min-w-0 space-y-1">
                  {result.population.by_grade.map((row) => (
                    <div key={row.grade} className="flex items-center gap-3 text-sm">
                      <span className="text-white/70 w-16 shrink-0">Grade {row.grade}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-white" style={{ width: `${Math.min(100, (row.count / Math.max(1, result.population.total_population)) * 100)}%` }} /></div>
                      <span className="font-bold w-6 text-right shrink-0">{row.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-white/40 text-xs mt-4">Population reflects all identical cards graded by MAS, counted per grade.</p>
            </div>

            {/* Card + details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
              <div className="lg:sticky lg:top-28">
                {result.cert.images?.[0]
                  ? <SlabPhoto src={result.cert.images[0]} alt={result.cert.subject} className="w-full max-w-[420px] mx-auto" />
                  : <SlabComingSoon cert={result.cert} className="w-full max-w-[420px] mx-auto" />}
              </div>
              <div className="bg-white border border-neutral-200 rounded-3xl p-7 shadow-sm">
                <div className="flex items-center gap-2 mb-4"><ShieldCheck className="text-[#0A0A0A]" size={18} /><span className="font-display font-bold text-lg text-[#0A0A0A]">Certificate Details</span></div>
                <Field label="Cert Number" value={result.cert.certnum} />
                <Field label="Grade" value={result.cert.grade} />
                <Field label="Label" value={result.cert.label} />
                <Field label="Year" value={result.cert.year} />
                <Field label="Brand" value={result.cert.brand} />
                <Field label="Subject" value={result.cert.subject} />
                <Field label="Card Number" value={result.cert.cardnum} />
                <Field label="Category" value={result.cert.category} />
                <Field label="Variant" value={result.cert.variant} />
                <Field label="Serial" value={result.cert.serial} />
              </div>
            </div>
          </motion.div>
        )}

        {/* EMPTY STATE */}
        {!result && !error && !loading && (
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-[#0A0A0A]">Instantly validate your grade</h2>
            <p className="text-neutral-500 mt-4 leading-relaxed max-w-2xl">
              Enter the certification number from your MAS graded slab to confirm its grade and authenticity. Our database provides real-time verification
              along with a full population report — so buyers and sellers can trust every card.
            </p>
            <div className="grid sm:grid-cols-3 gap-5 mt-12">
              {[
                { icon: ScanLine, t: "Tamper-evident", d: "Every card sealed in a secure, barcoded MAS holder." },
                { icon: Zap, t: "Real-time", d: "Instant lookup against the live MAS grading database." },
                { icon: BarChart3, t: "Population tracked", d: "See exactly how rare your card's grade truly is." },
              ].map((f) => (
                <div key={f.t} className="rounded-3xl border border-neutral-200 bg-[#FAFAFA] p-7">
                  <f.icon className="text-[#0A0A0A] mb-4" size={24} />
                  <h3 className="font-display font-bold text-lg text-[#0A0A0A]">{f.t}</h3>
                  <p className="text-neutral-500 text-sm mt-1.5">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
